import React, { useState, useEffect } from "react";

import { PageHero, Pagination, ViewToggle, type ViewMode } from "../components/common";
import BlogCard from "../components/blog/BlogCard";
import { Newsletter as NewsletterSection } from "../components/sections";
import { useNewsletterPosts } from "../hooks/useNewsletterPosts";
import type { BlogPost } from "../types";
import { formatDisplayDate } from "../utils/formatDisplayDate";

const POSTS_PER_PAGE = 6;
const FALLBACK_IMAGE = "/images/initiatives/Picture1.png";
const VIEW_STORAGE_KEY = "newsletter-view";

const Newsletter: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode | null;
    if (stored === "grid" || stored === "list") setViewMode(stored);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
  };

  const {
    posts: apiPosts,
    totalCount,
    loading,
  } = useNewsletterPosts(currentPage, POSTS_PER_PAGE);

  const mappedPosts: BlogPost[] = apiPosts.map((post) => ({
    slug: post.id ? String(post.id) : undefined,
    image: post.eng_thumbnail || FALLBACK_IMAGE,
    imageAlt: `${post.eng_title || "Newsletter"} Image`,
    title: post.eng_title || "Newsletter",
    excerpt: post.eng_caption || "",
    date: formatDisplayDate(
      (post as { created_at?: string }).created_at ||
        post.created_date ||
        post.published_at ||
        ""
    ),
    readTime: "",
    tags: Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag) => tag?.tag_name_eng)
          .filter((tagName): tagName is string => Boolean(tagName))
      : [],
    link: `/newsletter/${post.id ?? ""}`,
  }));

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || mappedPosts.length) / POSTS_PER_PAGE)
  );

  return (
    <main id="main-content" role="main">
      <PageHero
        title="Newsletter"
        subtitle="Stay updated with the latest AI literacy insights, resources, and updates from our hub"
        className="blog-hero"
      />

      <section
        className="blog-page-section"
        style={{ padding: "80px 0 100px 0", background: "white" }}
      >
        <div className="container">
          <div
            className="d-flex justify-content-end align-items-center mb-4"
            style={{ gap: "0.5rem" }}
          >
            <span className="text-muted" style={{ fontSize: "0.9rem", marginRight: "0.5rem" }}>
              View
            </span>
            <ViewToggle
              value={viewMode}
              onChange={handleViewChange}
              ariaLabel="Newsletter view: grid or list"
            />
          </div>
          <div className="row">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading newsletters...</span>
                </div>
              </div>
            ) : (
              mappedPosts?.map((post, index) => (
                <BlogCard key={post.slug ?? index} post={post} variant={viewMode} />
              ))
            )}
          </div>

          {!loading && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </section>

      <NewsletterSection />
    </main>
  );
};

export default Newsletter;
